// Levi Assistant Drawer + Launcher · shared · Feb 2026 · iter53.
//
// A small contractor-only slide-in drawer that opens from either:
//   * the persistent LeviLauncher pinned to the bottom-right corner of
//     the authenticated shell, OR
//   * the clickable Levi character on the /app/meet-levi page.
//
// The drawer intentionally REUSES the existing shared copy dictionary
// (`@/lib/leviIntro`) so what the contractor reads here matches the Meet
// Levi page and the Estimate Editor popover. On the Estimate Editor
// route, the existing embedded Levi.jsx panel remains the source of
// truth for advisories — the drawer is contextual guidance across the
// rest of the shell.
//
// Non-negotiable contract:
//   * ZERO api / fetch / LLM calls.
//   * ZERO mutations to estimate, project, line items, or customer-facing
//     surfaces.
//   * Never rendered outside the authenticated contractor shell.

import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, HelpCircle, ArrowRight } from "lucide-react";
import LeviCharacter from "@/components/LeviCharacter";
import LeviVoiceIntake from "@/components/levi/LeviVoiceIntake";
import LeviCommandConsole from "@/components/levi/LeviCommandConsole";
import LeviVoiceDiagPanel from "@/components/levi/LeviVoiceDiagPanel";
import LeviVoiceSupportActions from "@/components/levi/LeviVoiceSupportActions";
import MeetLeviFlow from "@/components/levi/MeetLeviFlow";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { primeTts, speakLeviReply, playServerTtsForReply, isDiagEnabled } from "@/lib/leviSpeech";
import { leviPreferredName } from "@/lib/leviName";
import {
  LEVI_INTRO_TITLE,
  LEVI_INTRO_TITLE_ES,
  LEVI_INTRO_HREF,
} from "@/lib/leviIntro";

// ---------------- Context ----------------
// A single context so the launcher, the Meet Levi character, and any other
// contractor surface all open the SAME drawer instance. There is one and
// only one drawer mounted per authenticated session.

const LeviDrawerCtx = createContext(null);

export const LeviDrawerProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  // LEVI-FIRST-01 v2 · Optional initial mode requested by the caller
  // that opens the drawer.
  //   "info"          → LEGACY value. LEVI-UNIFIED-01 retires the
  //                     two-choice landing screen; any caller that
  //                     still passes "info" (or nothing) now lands on
  //                     the single Levi conversation surface below.
  //   "command"       → the single Levi conversation surface
  //                     (LeviCommandConsole). Voice-first (Tap to
  //                     speak is the primary CTA).
  //   "command_type"  → same LeviCommandConsole surface but starting
  //                     in type-mode with the textarea focused and no
  //                     spoken greeting. Used by the Hero's
  //                     "Type to Levi" affordance.
  //   "voice_intake"  → the specialized *new-estimate* voice intake
  //                     surface. Reserved for the deterministic
  //                     "start a new estimate" router bridge and the
  //                     New Estimate page coach — never a peer choice
  //                     next to the general Levi conversation.
  //
  // LEVI-UNIFIED-01 default: every bare `openDrawer()` call resolves
  // to "command" (voice-first) so contractors never see a mode-select
  // screen. Old "info" callers are transparently upgraded.
  const [initialMode, setInitialMode] = useState("command");
  const openDrawer = useCallback((mode) => {
    const allowed = ["voice_intake", "command", "command_type"];
    setInitialMode(allowed.includes(mode) ? mode : "command");
    setOpen(true);
  }, []);
  const closeDrawer = useCallback(() => setOpen(false), []);

  // Auto-open bridge (PO 2026-08-08 · post-walk continuity fix).
  // useLeviCommand dispatches levi:auto-open-drawer when the finalized
  // Vision walk needs to hand back to the unified Levi drawer. The
  // drawer opens in the normal "command" surface; useLeviCommand then
  // submits walk_completed_session_id via the same /command pipeline
  // so Levi's reply renders in the same conversation.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onAutoOpen = () => {
      setInitialMode("command");
      setOpen(true);
    };
    // PO 2026-02 · Phase-5B first-login entry UX. The MeetLeviFlow
    // "Set up manually" choice dispatches this event so the drawer
    // closes before navigating to /app/setup. No circular import
    // needed; MeetLeviFlow doesn't touch LeviDrawerCtx directly.
    const onCloseRequest = () => setOpen(false);
    window.addEventListener("levi:auto-open-drawer", onAutoOpen);
    window.addEventListener("levi:close-drawer", onCloseRequest);
    return () => {
      window.removeEventListener("levi:auto-open-drawer", onAutoOpen);
      window.removeEventListener("levi:close-drawer", onCloseRequest);
    };
  }, []);

  // P0 RC-2 fix (PO 2026-08-09 real-iPhone reproduction). The
  // ``levi:vision-walk-stopped`` listener that submits
  // ``walk_completed_session_id`` used to live INSIDE ``useLeviCommand``
  // — which is only mounted while the drawer is open. On the
  // dedicated Vision page ``/app/estimates/new/levi?levi_camera=1``
  // the drawer is typically CLOSED so the contractor can focus on
  // the camera; when Finish Walkthrough fires, the event has no
  // subscriber and Levi never receives the post-walk handoff.
  //
  // This provider-level listener is always mounted (LeviDrawerProvider
  // wraps every authenticated route via DashboardLayout). When the
  // recorder emits vision-walk-stopped with finalized=true it:
  //   1. buffers the payload on ``window.__leviPendingPostWalk`` (a
  //      tiny bounded transport — cleared once useLeviCommand replays
  //      it),
  //   2. opens the drawer so LeviCommandConsole mounts on the next
  //      React commit,
  //   3. ``useLeviCommand``'s mount-time useEffect picks up the
  //      pending walk and calls submit({walkCompletedSessionId}) via
  //      the SAME pipeline it already uses.
  // No new state machine, no duplicate submit, no parallel drawer.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onWalkStopped = (ev) => {
      const d = (ev && ev.detail) || {};
      if (!d.finalized) return;
      const wsid = d.walk_session_id;
      const lsid = d.levi_estimate_session_id;
      if (!wsid || !lsid) return;
      // Bounded transport — plain object on window. Cleared by the
      // consumer inside useLeviCommand once replayed.
      try {
        window.__leviPendingPostWalk = {
          walk_session_id: wsid,
          levi_estimate_session_id: lsid,
          transcription_status: d.transcription_status || null,
          buffered_at: new Date().toISOString(),
        };
        // P0 REPAIR-2 (PO 2026-08-09) · timestamp the auto-open so
        // the drawer's greet-on-open effect can suppress its
        // time-of-day greeting without racing against the
        // ``useLeviCommand`` mount effect that consumes and clears
        // ``__leviPendingPostWalk`` synchronously. This value is
        // read by the greet effect and left to expire naturally
        // (stale after ~3 s) so subsequent manual opens still greet
        // normally.
        window.__leviPostWalkAutoOpenAt = Date.now();
      } catch { /* noop */ }
      // Open the drawer so LeviCommandConsole mounts. If the drawer
      // is already open, this is a no-op (setState with same value).
      setInitialMode("command");
      setOpen(true);
    };
    window.addEventListener("levi:vision-walk-stopped", onWalkStopped);
    return () => window.removeEventListener("levi:vision-walk-stopped", onWalkStopped);
  }, []);
  const value = useMemo(
    () => ({ open, openDrawer, closeDrawer, initialMode }),
    [open, openDrawer, closeDrawer, initialMode],
  );
  return <LeviDrawerCtx.Provider value={value}>{children}</LeviDrawerCtx.Provider>;
};

export const useLeviDrawer = () => {
  const ctx = useContext(LeviDrawerCtx);
  if (!ctx) {
    // Provider missing (e.g. public routes) — return no-op handles so the
    // launcher / character can safely be dropped in without crashing.
    return { open: false, openDrawer: () => {}, closeDrawer: () => {}, initialMode: "info" };
  }
  return ctx;
};

// ---------------- Drawer ----------------

export const LeviAssistantDrawer = () => {
  const { open, closeDrawer, initialMode } = useLeviDrawer();
  const { lang } = useLang();
  const { user } = useAuth();
  const es = lang === "es";
  const preferredVoiceId = user?.levi_preferences?.levi_voice_id === "warm"
    ? "warm"
    : "professional";
  // Slice LV-01 · voice-intake mode inside the same shared drawer. When
  // the drawer closes we always reset back to the informational default
  // so the next open starts fresh. LEVI-FIRST-01 v2 · Initialize mode
  // directly from the caller-requested initialMode so the drawer never
  // renders "info" content for one frame when a caller (e.g. the
  // Levi-primary hero) explicitly asks for voice_intake / command /
  // command_type.
  //
  // "command_type" is a presentation variant of "command" — the same
  // LeviCommandConsole surface but pre-flipped into type-mode with the
  // textarea focused. It exists so the hero's "Type to Levi" opens
  // straight into a text conversation instead of the voice-first
  // "Tap to speak" primary CTA.
  //
  // LEVI-UNIFIED-01 · Any caller that still passes "info" (or nothing)
  // lands on the single Levi conversation surface. The legacy
  // two-button choice screen is retired.
  const normalizedInitial = initialMode === "command_type"
    ? "command"
    : (initialMode === "voice_intake" ? "voice_intake" : "command");
  const [mode, setMode] = useState(normalizedInitial);
  const voiceDiagEnabled = isDiagEnabled();
  useEffect(() => {
    if (!open) {
      setMode("command");
    } else {
      setMode(normalizedInitial);
    }
  }, [open, normalizedInitial]);

  // Slice LV-02 · greet-on-open. When the drawer transitions from
  // closed to open, Levi speaks a single time-of-day greeting using
  // the shared spoken-reply pipeline. Opening the drawer and tapping
  // the microphone are two separate interactions — the greeting does
  // NOT play on mic press.
  //
  // LEVI-FIRST-01 v2 · Suppress the spoken greeting when the drawer
  // is opened via a "type" quick-action (initialMode === "command_type").
  // The contractor's intent there is to *type*, not to listen, and a
  // spoken greeting reads as noisy and voice-first for a text
  // interaction. Voice-forward openings (info panel, Talk-to-Levi,
  // shell button) continue to greet exactly as before.
  const greetedRef = useRef(false);
  const meetLeviActive = !!(user
    && user.levi_preferences
    && user.levi_preferences.levi_preferences_setup_done === false);
  useEffect(() => {
    if (!open) { greetedRef.current = false; return; }
    if (initialMode === "command_type") return;
    // Phase 7-D · when Meet Levi is showing, the flow's welcome copy
    // is the greeting; suppress the spoken one to keep the surface calm.
    if (meetLeviActive) return;
    // P0 REPAIR-2 (PO 2026-08-09) · If the drawer opened because a
    // Vision post-walk handoff is buffered, the immediate speech
    // must be the post-walk reply — not a generic time-of-day
    // greeting. Two competing Levi utterances were observed on the
    // real iPhone (`normalReply:speakRequested mode=drawer_greeting`
    // then `utterance:error code=canceled` then the real post-walk
    // reply). Silently suppress the greeting in this specific case;
    // manually opened drawers still greet exactly as before.
    //
    // We check BOTH ``__leviPendingPostWalk`` (which may already have
    // been consumed by useLeviCommand's mount effect — child effects
    // fire before parent effects, so the flag can be null by the
    // time this parent effect runs) AND ``__leviPostWalkAutoOpenAt``
    // (a monotonic timestamp set by LeviDrawerProvider at the exact
    // moment it opens the drawer for the walk handoff). The
    // timestamp expires naturally so subsequent manual opens greet
    // as normal.
    const _POSTWALK_GREET_SUPPRESS_MS = 3000;
    const _postWalkTs = (typeof window !== "undefined")
      ? window.__leviPostWalkAutoOpenAt : 0;
    const _postWalkFresh = _postWalkTs
      ? (Date.now() - _postWalkTs) < _POSTWALK_GREET_SUPPRESS_MS
      : false;
    if ((typeof window !== "undefined" && window.__leviPendingPostWalk)
        || _postWalkFresh) {
      try { greetedRef.current = true; } catch { /* noop */ }
      try {
        // eslint-disable-next-line no-console
        console.log("[LeviLauncher] greet suppressed: post-walk handoff pending");
      } catch { /* noop */ }
      return;
    }
    if (greetedRef.current) return;
    greetedRef.current = true;
    const firstName = leviPreferredName(user);
    const hour = new Date().getHours();
    const timeOfDay = es
      ? (hour < 12 ? "Buenos días" : hour < 18 ? "Buenas tardes" : "Buenas noches")
      : (hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening");
    const namePart = firstName ? `, ${firstName}` : "";
    const readyLine = es ? "Estoy listo cuando tú lo estés." : "I'm ready when you are.";
    const greeting = `${timeOfDay}${namePart}. ${readyLine}`;
    // PO 2026-08-30 · Voice consistency (correction B).
    // The drawer greeting now goes through the SAME server-TTS
    // path as conversational replies, using the authenticated
    // Warm/Professional preference instead of browser speechSynthesis,
    // so the contractor hears one consistent Levi voice from the
    // first spoken word onward. If the server route fails on the
    // greeting, we stay silent for that greeting only (no browser
    // TTS fallback) — a silent greeting is preferable to the
    // previous robotic Microsoft SAPI voice. `onPlaybackStart` is
    // deliberately omitted so this utterance does NOT arm the
    // conversational barge-in detector (mic is not open yet).
    const id = setTimeout(() => {
      playServerTtsForReply(greeting, lang || "en", {
        voice_id: preferredVoiceId,
        style: "balanced",
        speed: "normal",
        source: "drawer_greeting",
        mode: "drawer_greeting",
        // Silent-only fallback for the greeting: swallow the
        // reason without invoking browser speak(). Ordinary
        // conversation replies keep the standard fallback in
        // useLeviCommand.js.
        onFallback: () => { /* silent greeting fallback */ },
      });
    }, 120);
    return () => clearTimeout(id);
  }, [open, initialMode, user, lang, es, meetLeviActive, preferredVoiceId]);

  // Escape key closes the drawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") closeDrawer(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeDrawer]);

  if (!open) return null;

  const title    = es ? LEVI_INTRO_TITLE_ES    : LEVI_INTRO_TITLE;
  // LEVI-UNIFIED-01 · Contractor-friendly drawer subtitle. The
  // legacy "does / does not / where to find" panel is no longer
  // rendered in the drawer body — that reference content lives on
  // the Meet Levi page, still reachable from the drawer footer.
  const subtitle = es
    ? "Cuéntame lo que necesitas y me encargo. Tú decides."
    : "Tell me what you need and I'll handle it. You decide.";

  return (
    <div
      className="fixed inset-0 z-[100] flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="levi-drawer-title"
      data-testid="levi-drawer"
    >
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={closeDrawer}
        aria-hidden="true"
        data-testid="levi-drawer-overlay"
      />
      <aside
        className="relative w-full max-w-md bg-white shadow-2xl h-full flex flex-col animate-[slideIn_.25s_ease-out]"
        data-testid="levi-drawer-panel"
      >
        <header className="flex items-center gap-3 p-4 border-b border-[var(--jc-border)]" data-testid="levi-drawer-header">
          {/* LV-CONV-02 (2026-08-22) · PO decision — the small drawer
              header no longer reads clearly with a full-character
              photograph. Replaced with a clean free-floating paw
              mark in JobClose AI blue as Levi's visual signature.
              Inline SVG so no new asset file, no runtime request,
              no container/frame/scene. */}
          <svg
            viewBox="0 0 64 64"
            className="shrink-0 h-12 w-12 text-[var(--jc-blue)]"
            fill="currentColor"
            aria-hidden="true"
            data-testid="levi-drawer-paw-mark"
          >
            {/* heel pad */}
            <path d="M32 60c-6.6 0-11.5-3.4-11.5-8.2 0-3.6 2.4-6.6 5.1-9.4C28.1 40 30 37.3 30 34.2c0-1.9 1-3.4 2-3.4s2 1.5 2 3.4c0 3.1 1.9 5.8 4.4 8.2 2.7 2.8 5.1 5.8 5.1 9.4C43.5 56.6 38.6 60 32 60Z" />
            {/* toe pads (4) */}
            <ellipse cx="17" cy="27" rx="5.2" ry="6.8" />
            <ellipse cx="47" cy="27" rx="5.2" ry="6.8" />
            <ellipse cx="24" cy="12" rx="4.4" ry="6" />
            <ellipse cx="40" cy="12" rx="4.4" ry="6" />
          </svg>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[var(--jc-blue)]">
              {es ? "LEVI · TU COMPAÑERO" : "LEVI · YOUR COWORKER"}
            </div>
            <h2 id="levi-drawer-title" className="font-display text-base leading-tight mt-0.5">{title}</h2>
            <p className="text-xs text-[var(--jc-text-soft)] mt-0.5 leading-snug">{subtitle}</p>
          </div>
          <button
            onClick={closeDrawer}
            data-testid="levi-drawer-close-btn"
            aria-label={es ? "Cerrar Levi" : "Close Levi"}
            className="p-1.5 rounded-md text-[var(--jc-text-soft)] hover:bg-[var(--jc-bg)] hover:text-[var(--jc-text)] self-start"
          >            <X size={16} />
          </button>
        </header>
        <LeviVoiceSupportActions />
        <div className="flex-1 min-h-0 flex flex-col p-5 gap-5">
          {/* LV-02 · Voice diagnostic panel is gated behind an
              INTERNAL DEVELOPER FLAG (?leviDiag=1 or the sticky
              localStorage key jc:levi:diag=1). It never renders in
              the normal contractor experience. */}
          {voiceDiagEnabled && <LeviVoiceDiagPanel />}
          {/* Phase 7-D · Meet Levi first-run flow. Renders in place of
              the transcript when the flag is on and setup is not yet
              done. Skips finalize setup_done so this never reopens
              automatically. */}
          {user
            && user.levi_preferences
            && user.levi_preferences.levi_preferences_setup_done === false
            ? (
              <MeetLeviFlow onDone={() => { /* refetch handled via refreshMe */ }} />
            ) : (
              <div
                className={voiceDiagEnabled ? "flex-1 min-h-0" : "contents"}
                data-testid="levi-command-console-slot"
              >
                <LeviCommandConsole
                  onClose={closeDrawer}
                  startInTypeMode={initialMode === "command_type"}
                  mode={mode}
                  onSwitchMode={setMode}
                />
              </div>
            )}
        </div>
        <footer className="border-t border-[var(--jc-border)] p-4 flex items-center justify-between gap-2">
          {/* Phase 7 correction (2026-08-04) · when the Meet Levi
              first-run flow is showing (setup_done=false) the drawer
              body IS the setup surface, so this footer link is hidden
              to avoid misrouting to the legacy informational page.
              When setup is done, the link routes to the actual
              editable Levi settings page. */}
          {meetLeviActive ? (
            <span />
          ) : (
            <Link
              to="/app/settings/levi"
              onClick={closeDrawer}
              data-testid="levi-drawer-meet-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--jc-blue)] hover:underline"
            >
              <HelpCircle size={14} /> {es ? "Ajustes de Levi" : "Levi settings"} <ArrowRight size={12} />
            </Link>
          )}
          <button
            onClick={closeDrawer}
            data-testid="levi-drawer-done-btn"
            className="text-xs font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
          >
            {es ? "Cerrar" : "Close"}
          </button>
        </footer>
      </aside>
    </div>
  );
};

// ---------------- Launcher ----------------

// Route-visibility rules — the launcher is only rendered inside the
// authenticated shell (DashboardLayout is where it mounts) and is hidden
// on the Meet Levi page since the big character IS the launcher there.
const LAUNCHER_HIDDEN_ROUTES = [
  "/app/meet-levi",
];

export const LeviLauncher = () => {
  const { open, openDrawer } = useLeviDrawer();
  const { lang } = useLang();
  const location = useLocation();

  // Route visibility. Anything under DashboardLayout is authenticated by
  // definition, so we only need to skip the Meet Levi page here.
  if (LAUNCHER_HIDDEN_ROUTES.some((r) => location.pathname === r || location.pathname.startsWith(r + "/"))) {
    return null;
  }
  // Hide while the drawer is open so the launcher doesn't overlap the
  // drawer contents.
  if (open) return null;

  const label = lang === "es"
    ? "Conocer a Levi, tu compañero en JobClose AI"
    : "Meet Levi, your coworker at JobClose AI";

  // Slice LV-02 · prime speech synthesis synchronously within the
  // launcher tap gesture so the drawer's greeting is audible on iOS
  // Safari (which requires the FIRST speechSynthesis.speak call to
  // occur inside a user gesture). primeTts is idempotent per tab.
  const handleOpen = () => {
    primeTts("launcher-open");
    openDrawer();
  };

  return (
    <button
      type="button"
      onClick={handleOpen}
      aria-label={label}
      title={label}
      data-testid="levi-launcher"
      // Slice 2F1 · Levi presence upgrade. Grow the visible Levi to
      // approximately 1.75× (mobile 96 px) → 2× (tablet 112 px) → 2.25×
      // (desktop 128 px) of the previous 56 px size so contractors read
      // Levi as an AI Project Manager rather than a generic chatbot
      // launcher. Positioning classes (fixed bottom-24 right-4 z-40) are
      // preserved to keep clear of the mobile bottom-nav, the Emergent
      // preview badge, and scrollbars, and to remain below toasts. The
      // white ring + shadow-xl treatment reads as a portrait, not a
      // widget.
      className="fixed bottom-24 right-4 z-40 w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full bg-white shadow-xl ring-1 ring-[var(--jc-border-strong)] hover:ring-[var(--jc-blue)] hover:shadow-2xl transition flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] p-1"
    >
      <LeviCharacter mode="launcher" size={112} className="rounded-full" testid="levi-launcher-avatar" />
      <span className="sr-only">{label}</span>
    </button>
  );
};
